(() => {
  const q = window.makeStorefrontQuery;
  if (!q) {
    console.error('[Storefront] storefront-client.js must be loaded before storefront-queries');
    return;
  }

  const TTL_2M = 2 * 60 * 1000;
  const TTL_30M = 30 * 60 * 1000;

  async function productsByIds(productIds, { variantCount = 30 } = {}) {
    const ids = (productIds || []).map((id) => window.toGID(id));
    if (!ids.length) return [];

    const query = `
      query ProductsByIds($ids: [ID!]!, $variantCount: Int!) {
        nodes(ids: $ids) {
          __typename
          ... on Product {
            id
            handle
            title
            availableForSale
            featuredImage { url altText }
            priceRange {
              minVariantPrice { amount currencyCode }
              maxVariantPrice { amount currencyCode }
            }
            variants(first: $variantCount) {
              nodes {
                id
                title
                availableForSale
                selectedOptions { name value }
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
              }
            }
          }
        }
      }`;

    const data = await q(query, { ids, variantCount }, { cacheTTL: TTL_2M });

    return (data?.nodes || [])
      .filter((n) => n && n.__typename === 'Product')
      .map((p) => {
        const variants = p.variants?.nodes || [];
        const price = p.priceRange?.minVariantPrice?.amount ?? null;
        const compareAtMin = variants
          .map((v) => v.compareAtPrice?.amount)
          .filter(Boolean)
          .map(Number);
        const compare_at_price = compareAtMin.length ? String(Math.min(...compareAtMin)) : null;

        return {
          id: p.id,
          handle: p.handle,
          title: p.title,
          price, // string amount
          compare_at_price, // string amount or null
          featured_image: p.featuredImage?.url || null,
          variants: variants.map((v) => ({
            id: v.id,
            title: v.title,
            available: v.availableForSale,
            price: v.price?.amount ?? null,
            compare_at_price: v.compareAtPrice?.amount ?? null,
            selectedOptions: v.selectedOptions || [],
          })),
          available: p.availableForSale,
        };
      });
  }

  async function getProductMetafieldReferences(idOrGid, namespace, key, queryName, { first = 20 } = {}) {
    const id = window.toGID(idOrGid);
    const query = `
      query ${queryName}($id: ID!, $first: Int!) {
        product(id: $id) {
          metafield(namespace: "${namespace}", key: "${key}") {
            value
            references(first: $first) {
              nodes {
                ... on Product {
                  id
                  handle
                  title
                  variants(first: 1) { nodes { id availableForSale } }
                }
              }
            }
          }
        }
      }`;
    return await q(query, { id, first }, { cacheTTL: TTL_2M });
  }

  // Every cart-drawer metafield of one product in a single cached query.
  async function productCartMetaById(idOrGid) {
    const id = window.toGID(idOrGid);
    const query = `
      query ProductCartMeta($id: ID!) {
        product(id: $id) {
          badge: metafield(namespace: "judgeme", key: "badge") { value }
          blurb: metafield(namespace: "custom", key: "blurb") { value }
          preorder: metafield(namespace: "custom", key: "preorder_text") { value }
          hideSubscribe: metafield(namespace: "custom", key: "hide_subscribe_and_save_button_from_minicart") { value }
          bundle: metafield(namespace: "custom", key: "bundle") {
            reference {
              ... on Metaobject {
                fields { key references(first: 20) { nodes { ... on ProductVariant { id } } } }
              }
            }
          }
        }
      }`;
    const data = await q(query, { id }, { cacheTTL: TTL_30M });
    return data?.product || null;
  }

  // Product id, handle and tags for variant ids (gift variants from gwp_settings),
  // the data a cart line render needs before the line exists.
  async function productTagsByVariantIds(variantIds) {
    const ids = (variantIds || []).map((id) => window.toGID(id, 'ProductVariant'));
    if (!ids.length) return [];
    const query = `
      query ProductTagsByVariantIds($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            product { id handle tags }
          }
        }
      }`;
    const data = await q(query, { ids }, { cacheTTL: TTL_30M });
    return (data?.nodes || [])
      .map((node) => node?.product)
      .filter((product) => product && product.handle)
      .map((product) => ({
        id: Number(String(product.id).split('/').pop()),
        handle: product.handle,
        tags: product.tags || [],
      }));
  }

  // Title, image and compare-at value of gift variants, for gift cards shown before the gift is in the cart.
  async function giftVariantsByIds(variantIds) {
    const ids = (variantIds || []).map((id) => window.toGID(id, 'ProductVariant'));
    if (!ids.length) return [];
    const query = `
      query GiftVariantsByIds($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            image { url }
            compareAtPrice { amount }
            product { title featuredImage { url } }
          }
        }
      }`;
    const data = await q(query, { ids }, { cacheTTL: TTL_30M });
    return (data?.nodes || [])
      .filter((node) => node && node.product)
      .map((node) => {
        const compareAt = Number(node.compareAtPrice?.amount);
        return {
          id: Number(String(node.id).split('/').pop()),
          productTitle: node.product.title,
          image: node.image?.url || node.product.featuredImage?.url || null,
          compareAtCents: Number.isFinite(compareAt) && compareAt > 0 ? Math.round(compareAt * 100) : null,
        };
      });
  }

  const STARTER_METAFIELD_KEYS = ['starter_multivitamin', 'starter_biotic', 'starter_magnesium'];

  // Numeric variant ids of the product's Bundle entry, category_1..N_products in order.
  function bundleVariantIds(meta) {
    return (meta?.bundle?.reference?.fields || [])
      .map((field) => ({ order: Number((/^category_(\d+)_products$/.exec(field.key) || [])[1]), field }))
      .filter((entry) => entry.order > 0)
      .sort((a, b) => a.order - b.order)
      .flatMap((entry) => entry.field.references?.nodes || [])
      .map((node) => String(node?.id || '').split('/').pop())
      .filter(Boolean);
  }

  // Bundle items keyed by the three starter slots the cart upsell and quiz add paths read.
  // Bundles with more than three items are box-builder catalogs, not pack contents.
  async function productStarterMetafieldsById(idOrGid) {
    const ids = bundleVariantIds(await productCartMetaById(idOrGid));
    if (ids.length > STARTER_METAFIELD_KEYS.length) return {};
    return STARTER_METAFIELD_KEYS.reduce((acc, key, index) => {
      if (ids[index]) acc[key] = ids[index];
      return acc;
    }, {});
  }

  async function productSidecartUpsellsById(idOrGid, options = {}) {
    const data = await getProductMetafieldReferences(idOrGid, 'custom', 'sidecart_upsell_products', 'SidecartUpsells', options);
    return data?.product?.metafield?.references?.nodes ?? [];
  }

  async function productReviewsById(idOrGid) {
    const meta = await productCartMetaById(idOrGid);
    return meta?.badge?.value || null;
  }

  async function productBlurbById(idOrGid) {
    const meta = await productCartMetaById(idOrGid);
    return meta?.blurb?.value || null;
  }

  async function productPreorderTextById(idOrGid) {
    const meta = await productCartMetaById(idOrGid);
    const value = String(meta?.preorder?.value ?? '').trim();
    return value || null;
  }

  // Product preorder text for variant ids, keyed by numeric variant id ('' when the product has none).
  async function preorderTextByVariantIds(variantIds) {
    const ids = (variantIds || []).map((id) => window.toGID(id, 'ProductVariant'));
    if (!ids.length) return {};
    const query = `
      query PreorderTextByVariantIds($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            product {
              preorder: metafield(namespace: "custom", key: "preorder_text") { value }
            }
          }
        }
      }`;
    const data = await q(query, { ids }, { cacheTTL: TTL_30M });
    const texts = {};
    ids.forEach((gid) => { texts[gid.split('/').pop()] = ''; });
    (data?.nodes || []).forEach((node) => {
      if (!node?.id) return;
      texts[String(node.id).split('/').pop()] = String(node.product?.preorder?.value ?? '').trim();
    });
    return texts;
  }

  async function productHideSubscribeButtonById(idOrGid) {
    const meta = await productCartMetaById(idOrGid);
    const value = meta?.hideSubscribe?.value;
    return value === 'true' || value === true;
  }

  async function productAddOnPricingById(idOrGid) {
    const id = window.toGID(idOrGid);
    const query = `
      query ProductAddOnPricing($id: ID!) {
        product(id: $id) {
          promo: metafield(namespace: "custom", key: "bfcm_add_on_promo_price") {
            value
          }
          full: metafield(namespace: "custom", key: "bfcm_add_on_full_price") {
            value
          }
        }
      }`;

    const data = await q(query, { id }, { cacheTTL: TTL_2M });
    const product = data?.product;

    return {
      promo: product?.promo?.value ?? null,
      full: product?.full?.value ?? null
    };
  }

  window.Storefront = {
    productsByIds,
    productCartMetaById,
    productTagsByVariantIds,
    giftVariantsByIds,
    productSidecartUpsellsById,
    productReviewsById,
    productBlurbById,
    productPreorderTextById,
    preorderTextByVariantIds,
    productStarterMetafieldsById,
    productHideSubscribeButtonById,
    productAddOnPricingById,
  };

  q.productsByIds = productsByIds;
  q.productCartMetaById = productCartMetaById;
  q.productTagsByVariantIds = productTagsByVariantIds;
  q.giftVariantsByIds = giftVariantsByIds;
  q.productSidecartUpsellsById = productSidecartUpsellsById;
  q.productReviewsById = productReviewsById;
  q.productBlurbById = productBlurbById;
  q.productPreorderTextById = productPreorderTextById;
  q.preorderTextByVariantIds = preorderTextByVariantIds;
  q.productStarterMetafieldsById = productStarterMetafieldsById;
  q.productAddOnPricingById = productAddOnPricingById;
})();
